import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { VoiceFilters } from '@/features/voice/domain/models/voice-filters'
import { useAppStore } from '@/shared/store/app.store'

interface VoiceFiltersStore {
  filters: VoiceFilters
  searchTerm: string
}

interface PersistedVoiceFiltersStore {
  filters: Omit<VoiceFilters, 'technologies'>
  searchTerm: string
}

const DEFAULT_STATE: VoiceFiltersStore = { filters: {}, searchTerm: '' }

function isPersistedVoiceFiltersStore(
  value: unknown
): value is PersistedVoiceFiltersStore {
  return (
    typeof value === 'object' &&
    value !== null &&
    'filters' in value &&
    typeof (value as Record<string, unknown>).filters === 'object' &&
    'searchTerm' in value &&
    typeof (value as Record<string, unknown>).searchTerm === 'string'
  )
}

// Voice search/filters must not leak between orgs sharing the same browser
// (Pro Editor can switch org without a full page reload), so the storage key
// is scoped to whichever org is active when it's read/written.
const storageKeyForCurrentOrg = (): string => {
  const orgId = useAppStore.getState().org
  return orgId ? `voices-filters-${orgId}` : 'voices-filters'
}

const orgScopedStorage = createJSONStorage<PersistedVoiceFiltersStore>(() => ({
  getItem: () => localStorage.getItem(storageKeyForCurrentOrg()),
  setItem: (_name: string, value: string) => {
    localStorage.setItem(storageKeyForCurrentOrg(), value)
  },
  removeItem: () => {
    localStorage.removeItem(storageKeyForCurrentOrg())
  },
}))

export const useVoiceFiltersStore = create(
  persist<VoiceFiltersStore, [], [], PersistedVoiceFiltersStore>(
    () => ({ filters: {}, searchTerm: '' }),
    {
      name: 'voices-filters',
      storage: orgScopedStorage,
      partialize: (state): PersistedVoiceFiltersStore => {
        const { technologies: _technologies, ...filtersWithoutTechnologies } =
          state.filters
        return {
          filters: filtersWithoutTechnologies,
          searchTerm: state.searchTerm,
        }
      },
      // Never fall back to the pre-rehydrate in-memory state here: it can
      // still hold another org's search/filters when the org just switched.
      merge: (persistedState) =>
        isPersistedVoiceFiltersStore(persistedState)
          ? { ...DEFAULT_STATE, ...persistedState }
          : DEFAULT_STATE,
    }
  )
)
